/**
 * COST EXPLORER - Detailed filtering and data export
 */

import React, { useState, useEffect } from 'react';
import { Search, Download, RefreshCw, AlertCircle, Database, Filter, TrendingUp } from 'lucide-react';
import FilterPanel from '../components/admin/financials/FilterPanel';
import CostDataTable from '../components/admin/financials/CostDataTable';
import FinancialNav from '../components/admin/financials/FinancialNav';
import TenantSelector from '../components/admin/financials/TenantSelector';
import apiClient from '../lib/apiClient';

export default function CostExplorer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('event_timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [summary, setSummary] = useState({ total_cost: 0 });

  useEffect(() => {
    loadData();
  }, [filters, pagination.page, sortBy, sortOrder, tenantFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Convert filters to API format (arrays to comma-separated strings, plural to singular)
      const apiParams = {
        page: pagination.page,
        limit: pagination.limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      // Handle array filters - convert to comma-separated strings
      if (filters.providers && filters.providers.length > 0) {
        apiParams.provider = Array.isArray(filters.providers)
          ? filters.providers.join(',')
          : filters.providers;
      }
      if (filters.event_types && filters.event_types.length > 0) {
        apiParams.event_type = Array.isArray(filters.event_types)
          ? filters.event_types.join(',')
          : filters.event_types;
      }
      if (filters.date_from) {
        apiParams.date_from = filters.date_from;
      }
      if (filters.date_to) {
        apiParams.date_to = filters.date_to;
      }

      const params = new URLSearchParams(apiParams);

      if (tenantFilter !== 'all') {
        params.append('tenant_id', tenantFilter);
      }

      const response = await apiClient.get(`/admin/financials/cost-events?${params}`);

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      setData(response.data?.data || []);
      setSummary(response.data?.summary || { total_cost: 0 });
      setPagination(response.data?.pagination || { page: 1, limit: 50, total: 0 });
    } catch (err) {
      console.error('Error loading cost events:', err);
      setError(err.message || 'Failed to load cost events');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));  // Reset to page 1
  };

  const handleFilterReset = () => {
    setFilters({});
    setTenantFilter('all');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (column, direction) => {
    setSortBy(column);
    setSortOrder(direction);
  };

  const handleExport = async () => {
    try {
      const apiParams = { format: 'csv' };

      if (filters.providers && filters.providers.length > 0) {
        apiParams.provider = filters.providers.join(',');
      }
      if (filters.event_types && filters.event_types.length > 0) {
        apiParams.event_type = filters.event_types.join(',');
      }
      if (filters.date_from) apiParams.date_from = filters.date_from;
      if (filters.date_to) apiParams.date_to = filters.date_to;
      if (tenantFilter !== 'all') apiParams.tenant_id = tenantFilter;

      const params = new URLSearchParams(apiParams);
      const response = await apiClient.get(`/admin/financials/export?${params}`, {
        responseType: 'blob'
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `cost-events-${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error exporting data:', err);
      alert('Error exporting data: ' + (err.response?.data ? 'Server error' : err.message));
    }
  };

  const columns = [
    { key: 'event_timestamp', label: 'Date/Time', format: 'datetime' },
    { key: 'tenant_id', label: 'Customer ID', render: (val) => val?.substring(0, 8) + '...' },
    { key: 'event_type', label: 'Event Type', render: (val) => val?.replace(/_/g, ' ') },
    { key: 'provider', label: 'Provider', render: (val) => <span className="capitalize">{val}</span> },
    { key: 'quantity', label: 'Quantity', format: 'number' },
    { key: 'unit_cost', label: 'Unit Cost', format: 'currency' },
    { key: 'total_cost', label: 'Total Cost', format: 'currency' }
  ];

  // Calculate summary stats
  const totalCost = parseFloat(summary.total_cost || 0);
  const totalEvents = pagination.total;
  const avgCost = totalEvents > 0 ? totalCost / totalEvents : 0;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              🔍 Cost Explorer
            </h1>
            <p className="text-gray-600 mt-2">Filter and analyze cost events in detail</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              disabled={loading || data.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tenant Filter */}
        <div className="flex gap-4 items-center p-4 bg-white rounded-lg shadow-sm">
          <Filter className="w-5 h-5 text-gray-400" />
          <TenantSelector value={tenantFilter} onChange={setTenantFilter} />
          <div className="flex-1"></div>
          <button
            onClick={handleFilterReset}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Reset All Filters
          </button>
        </div>
      </div>

      {/* Navigation */}
      <FinancialNav />

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Failed to load cost events</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Try again →
            </button>
          </div>
        </div>
      )}

      {/* Layout: Filter Panel + Content */}
      <div className="flex gap-6">
        {/* Filter Panel */}
        <div className="flex-shrink-0">
          <FilterPanel
            onApply={handleFilterApply}
            onReset={handleFilterReset}
            availableFilters={{ providers: true, event_types: true, tenants: false }}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-sm p-6 border-2 border-blue-200 hover:border-blue-300 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-blue-700">📊 Total Events</p>
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              {loading && !data.length ? (
                <div className="h-8 w-24 bg-blue-200 rounded animate-pulse"></div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">{totalEvents.toLocaleString()}</p>
              )}
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm p-6 border-2 border-green-200 hover:border-green-300 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-green-700">💰 Total Cost</p>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              {loading && !data.length ? (
                <div className="h-8 w-32 bg-green-200 rounded animate-pulse"></div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">${totalCost.toFixed(2)}</p>
              )}
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-sm p-6 border-2 border-purple-200 hover:border-purple-300 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-purple-700">⚡ Avg Cost</p>
                <Search className="w-5 h-5 text-purple-600" />
              </div>
              {loading && !data.length ? (
                <div className="h-8 w-28 bg-purple-200 rounded animate-pulse"></div>
              ) : (
                <p className="text-3xl font-bold text-gray-900">${avgCost.toFixed(4)}</p>
              )}
            </div>
          </div>

          {/* Data Table */}
          <CostDataTable
            data={data}
            columns={columns}
            sortable={true}
            pagination={pagination}
            onPageChange={handlePageChange}
            onSort={handleSort}
            onExport={handleExport}
            isLoading={loading}
          />
        </div>
      </div>
    </div>
  );
}
